import { useMotionValue } from 'framer-motion';
import { useEffect, type RefObject } from 'react';

/**
 * Tracks the scroll progress (0-1) of an element.
 *
 * Returns null when there is no scrollable content.
 */
export function useScrollProgress(containerRef: RefObject<HTMLElement>) {
  const scrollYProgress = useMotionValue<number | null>(null);
  const scrollXProgress = useMotionValue<number | null>(null);

  useEffect(() => {
    const element = containerRef.current;

    if (!element) {
      return undefined;
    }

    const updateScrollProgress = () => {
      const hasVerticalScroll = element.scrollHeight > element.clientHeight;
      const hasHorizontalScroll = element.scrollWidth > element.clientWidth;

      if (hasVerticalScroll) {
        const scrollableHeight = element.scrollHeight - element.clientHeight;
        const progress = scrollableHeight > 0 ? element.scrollTop / scrollableHeight : 0;
        scrollYProgress.set(Math.min(Math.max(progress, 0), 1));
      } else {
        scrollYProgress.set(null);
      }

      if (hasHorizontalScroll) {
        const scrollableWidth = element.scrollWidth - element.clientWidth;
        const progress = scrollableWidth > 0 ? element.scrollLeft / scrollableWidth : 0;
        scrollXProgress.set(Math.min(Math.max(progress, 0), 1));
      } else {
        scrollXProgress.set(null);
      }
    };

    updateScrollProgress();

    element.addEventListener('scroll', updateScrollProgress);

    const resizeObserver = new ResizeObserver(updateScrollProgress);
    resizeObserver.observe(element);

    return () => {
      element.removeEventListener('scroll', updateScrollProgress);
      resizeObserver.disconnect();
    };
  }, [containerRef, scrollYProgress, scrollXProgress]);

  return {
    scrollYProgress,
    scrollXProgress,
  };
}
